import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect, useRef } from "react";
import { Plus, Minus, X, Download } from "lucide-react";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { rootRoute } from "./__root";
import { OfferFormPage } from "./offers.new";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Input } from "../components/ui/Input/Input";
import { Select } from "../components/ui/Select/Select";
import { FormField } from "../components/ui/FormField/FormField";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import { useVatGroups } from "../hooks/use-vat-groups";
import { ShippingAddressPicker } from "../components/ui/ShippingAddressPicker/ShippingAddressPicker";
import {
	useCreateOrder,
	useUpdateOrder,
	useOrderDetail,
	useOrderAttachments,
	useUploadOrderAttachments,
	useDeleteOrderAttachment,
	orderAttachmentDownloadUrl,
	type OrderAttachment,
} from "../hooks/use-orders";
import { FileDropzone } from "../components/ui/FileDropzone/FileDropzone";
import { useFormDraft } from "../hooks/use-form-draft";
import { useDelegation } from "../hooks/use-delegation";
import { api } from "../lib/api";
import { useToast } from "../lib/use-toast";
import { PANEL_GROUP_NAMES, isGeneratedLine, parsePanelLabel } from "../lib/panel-lines";
import styles from "./OrderNew.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/orders/new",
	component: withPermission(OrderNewPage, "orders", "create"),
});

const ORDER_STATUS_LABELS: Record<string, string> = {
	draft: "Açık (Taslak)",
	confirmed: "Açık",
	partially_delivered: "Açık (Kısmi Sevk)",
	delivered: "Açık (Sevk Edildi)",
	invoiced: "Açık (Faturalandı)",
	closed: "Kapalı",
	cancelled: "İptal Edildi",
};

interface StockItemWarehouse {
	warehouseId: string;
	warehouseName: string;
	warehouseCode: string;
	currentStock: string | number;
}

interface LineItem {
	lineNumber: number;
	itemCode: string;
	itemDescription: string;
	displayDescription: string;
	lineDetail: string;
	quantity: string;
	unit: string;
	unitPrice: string;
	vatRate: string;
	vatGroup: string;
	discountPercent: string;
	notes: string;
	atsDescription: string;
	tmsDescription: string;
	factorySpecialNotes: string;
	generalNotes: string;
	stockItemId: string;
	warehouseId: string;
	isCustomDesign: boolean;
}

type GeneratedLine = Omit<LineItem, "lineNumber">;

function toGeneratedLine(l: {
	itemCode?: string | null;
	itemDescription?: string | null;
	displayDescription?: string | null;
	lineDetail?: string | null;
	quantity?: string | null;
	unit?: string | null;
	unitPrice?: string | null;
	vatRate?: string | null;
	vatGroup?: string | null;
	discountPercent?: string | null;
	notes?: string | null;
	stockItemId?: string | null;
	warehouseId?: string | null;
}): GeneratedLine {
	return {
		itemCode: l.itemCode ?? "",
		itemDescription: l.itemDescription ?? "",
		displayDescription: l.displayDescription ?? "",
		lineDetail: l.lineDetail ?? "",
		quantity: l.quantity ?? "1",
		unit: l.unit ?? "",
		unitPrice: l.unitPrice ?? "",
		vatRate: l.vatRate ?? "20",
		vatGroup: l.vatGroup ?? "",
		discountPercent: l.discountPercent ?? "0",
		notes: l.notes ?? "",
		atsDescription: "",
		tmsDescription: "",
		factorySpecialNotes: "",
		generalNotes: "",
		stockItemId: "",
		warehouseId: l.warehouseId ?? "",
		isCustomDesign: false,
	};
}

function generatedLineLabel(line: GeneratedLine): string {
	const text = line.displayDescription || line.itemDescription || "";
	const parsed = parsePanelLabel(text);
	if (!parsed) return text;
	return parsed.qty > 1
		? `${parsed.group}: ${parsed.option} × ${parsed.qty}`
		: `${parsed.group}: ${parsed.option}`;
}

const ATS_PANEL_GROUPS = ["Change Over ATS", "ATS"];
const TMS_PANEL_GROUPS = ["CB"];

function joinPanelText(accessories: GeneratedLine[], groups: string[]): string {
	const parts = accessories
		.map((a) => a.itemDescription || a.displayDescription || "")
		.filter((desc) => groups.some((g) => desc.startsWith(`${g} - `)));
	return parts.join(" | ").slice(0, 150);
}

function OrderNewPage() {
	const search = route.useSearch() as {
		companyId?: string;
		contactPersonId?: string;
		sourceOfferId?: string;
		copyFrom?: string;
	};
	if (search.copyFrom) return <OfferFormPage copyOrderId={search.copyFrom} />;
	return <OrderFormPage initialSearch={search} />;
}

export function OrderFormPage({
	editId,
	copyFromId,
	initialSearch,
}: {
	editId?: string;
	copyFromId?: string | undefined;
	initialSearch?: {
		companyId?: string;
		contactPersonId?: string;
		sourceOfferId?: string;
	};
}) {
	const { t } = useTranslation();
	const { showToast } = useToast();
	const navigate = useNavigate();
	const createOrder = useCreateOrder();
	const updateOrder = useUpdateOrder();
	const isEdit = !!editId;
	const detail = useOrderDetail(editId ?? copyFromId ?? "");
	const [pendingFiles, setPendingFiles] = useState<File[]>([]);
	const existingAttachments = useOrderAttachments(isEdit ? (editId ?? null) : null);
	const uploadAttachments = useUploadOrderAttachments();
	const deleteAttachment = useDeleteOrderAttachment();
	const [removedAttachments, setRemovedAttachments] = useState<OrderAttachment[]>([]);
	const isRemovedAttachment = (item: OrderAttachment) =>
		removedAttachments.some((r) =>
			r.source === "sap" && item.source === "sap"
				? r.line === item.line
				: r.source === "pending" && item.source === "pending"
					? r.index === item.index
					: false,
		);
	const [prefilled, setPrefilled] = useState(false);
	// Kopyalanan siparişin formda görünmeyen alanları; yeni kayda olduğu gibi taşınır.
	const [copyExtras, setCopyExtras] = useState<{
		internalNotes?: string | undefined;
		tmsDescription?: string | undefined;
		atsDescription?: string | undefined;
		factorySpecialNotes?: string | undefined;
		generalNotes?: string | undefined;
	}>({});
	const { canDelegate, users: delegatableUsers } = useDelegation();
	const [onBehalfOf, setOnBehalfOf] = useState("");
	const search = initialSearch ?? {};
	const [header, setHeader] = useState({
		companyId: search.companyId ?? "",
		contactPersonId: search.contactPersonId ?? "",
		sourceOfferId: search.sourceOfferId ?? "",
		orderDate: "",
		deliveryDate: "",
		currency: "TL",
		paymentTerms: "",
		deliveryTerms: "",
		shippingAddress: "",
		shippingAddressId: "",
		notes: "",
		startupDescription: "",
		installationDescription: "",
		status: "confirmed",
	});
	const [initialOptions, setInitialOptions] = useState<
		Record<string, SearchableSelectOption | undefined>
	>({});
	const [formError, setFormError] = useState("");
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	// Firma listeden para birimiyle (ör. "Firma (EUR)") seçildiyse, firmanın varsayılan
	// para birimi bu seçimin üzerine yazılmamalı.
	const companyCurrencyPickedRef = useRef(false);

	useEffect(() => {
		const opts: Record<string, SearchableSelectOption> = {};
		const fetches: Promise<void>[] = [];
		if (header.companyId) {
			fetches.push(
				api
					.get<{ id: string; legalName: string; currency: string | null }>(
						`/api/companies/${header.companyId}`,
					)
					.then((c) => {
						const applyDefault = !isEdit && !companyCurrencyPickedRef.current && !!c.currency;
						const currency = applyDefault
							? (c.currency as string)
							: header.currency || c.currency || "";
						opts.company = {
							value: `${c.id}::${currency}`,
							label: `${c.legalName}${currency ? ` (${currency})` : ""}`,
							meta: { currency },
						};
						if (applyDefault) {
							setHeader((prev) => ({ ...prev, currency }));
						}
					})
					.catch(() => {}),
			);
		}
		if (header.contactPersonId) {
			fetches.push(
				api
					.get<{ id: string; firstName: string; lastName: string }>(
						`/api/persons/${header.contactPersonId}`,
					)
					.then((p) => {
						opts.person = { value: p.id, label: `${p.firstName} ${p.lastName}` };
					})
					.catch(() => {}),
			);
		}
		if (header.sourceOfferId) {
			fetches.push(
				api
					.get<{ id: string; documentNumber: string | null }>(`/api/offers/${header.sourceOfferId}`)
					.then((o) => {
						opts.offer = { value: o.id, label: o.documentNumber ?? o.id.slice(0, 8) };
					})
					.catch(() => {}),
			);
		}
		if (fetches.length === 0) return;
		Promise.all(fetches).then(() => setInitialOptions((prev) => ({ ...prev, ...opts })));
	}, [header.companyId, header.contactPersonId, header.sourceOfferId]);

	// Muhatap seçilince firmanın yetkili kişisi otomatik gelir.
	useEffect(() => {
		if (!header.companyId || header.contactPersonId) return;
		if (isEdit && !prefilled) return;
		let cancelled = false;
		api
			.get<{
				items: Array<{ id: string; firstName: string; lastName: string; isPrimary?: boolean }>;
			}>(`/api/persons?limit=50&companyId=${header.companyId}`)
			.then((res) => {
				if (cancelled) return;
				const primary = res.items.find((p) => p.isPrimary) ?? res.items[0];
				if (!primary) return;
				setHeader((prev) =>
					prev.companyId === header.companyId && !prev.contactPersonId
						? { ...prev, contactPersonId: primary.id }
						: prev,
				);
				setInitialOptions((prev) => ({
					...prev,
					person: { value: primary.id, label: `${primary.firstName} ${primary.lastName}` },
				}));
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [header.companyId, header.contactPersonId, isEdit, prefilled]);
	// Ana kalem indeksine göre, o kalemin altında taşınan üretilmiş satırlar.
	const [generatedLines, setGeneratedLines] = useState<Record<number, GeneratedLine[]>>({});
	const [lines, setLines] = useState<LineItem[]>([
		{
			lineNumber: 1,
			itemCode: "",
			itemDescription: "",
			displayDescription: "",
			lineDetail: "",
			quantity: "1",
			unit: "",
			unitPrice: "",
			vatRate: "20",
			vatGroup: "",
			discountPercent: "0",
			notes: "",
			atsDescription: "",
			tmsDescription: "",
			factorySpecialNotes: "",
			generalNotes: "",
			stockItemId: "",
			warehouseId: "",
			isCustomDesign: false,
		},
	]);
	const [warehouseStocks, setWarehouseStocks] = useState<Record<number, StockItemWarehouse[]>>({});
	const [allWarehouses, setAllWarehouses] = useState<{ id: string; code: string; name: string }[]>(
		[],
	);

	useEffect(() => {
		void (async () => {
			try {
				const res = await api.get<{
					items: { id: string; code: string; name: string; isActive: boolean }[];
				}>(`/api/warehouses?limit=200`);
				setAllWarehouses(
					(res.items ?? [])
						.filter((w) => w.isActive)
						.map((w) => ({ id: w.id, code: w.code, name: w.name })),
				);
			} catch {
				setAllWarehouses([]);
			}
		})();
	}, []);

	const { vatGroups, options: vatGroupOptions } = useVatGroups();

	// Hazır gelen satırlarda (düzenleme/taslak) stok kalemi seçicisi yalnızca id'yi bilir; etiketi
	// göstermek için satırdaki kod/açıklamadan seçenek üretiyoruz.
	const [stockItemOptions, setStockItemOptions] = useState<Record<string, SearchableSelectOption>>(
		{},
	);
	const rememberStockItemOptions = (
		rows: { stockItemId: string; itemCode: string; itemDescription: string }[],
	) => {
		const opts: Record<string, SearchableSelectOption> = {};
		for (const l of rows) {
			if (!l.stockItemId) continue;
			const label = [l.itemCode, l.itemDescription].filter(Boolean).join(" — ");
			opts[l.stockItemId] = { value: l.stockItemId, label: label || l.stockItemId };
		}
		if (Object.keys(opts).length > 0) setStockItemOptions((prev) => ({ ...prev, ...opts }));
	};

	interface OrderDraft {
		header: typeof header;
		lines: typeof lines;
	}
	const draft = useFormDraft<OrderDraft>("order-draft", !isEdit && !copyFromId);

	const restoreDraft = () => {
		if (!draft.pendingDraft) return;
		companyCurrencyPickedRef.current = !!draft.pendingDraft.header.companyId;
		setHeader(draft.pendingDraft.header);
		setLines(draft.pendingDraft.lines);
		rememberStockItemOptions(draft.pendingDraft.lines);
		draft.acceptDraft();
	};

	useEffect(() => {
		if (draft.showPrompt || copyFromId) return;
		const hasData = !!(
			header.companyId ||
			header.contactPersonId ||
			header.notes ||
			lines.some((l) => l.itemCode || l.itemDescription || l.stockItemId || Number(l.unitPrice) > 0)
		);
		if (hasData) draft.save({ header, lines });
		else draft.clear();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [header, lines, draft.showPrompt]);

	useEffect(() => {
		if ((!isEdit && !copyFromId) || prefilled || !detail.data) return;
		const o = detail.data;
		const d = (v: string | null) => (v ? v.slice(0, 10) : "");
		const isCopy = !isEdit;
		if (isCopy) {
			setCopyExtras({
				internalNotes: o.internalNotes ?? undefined,
				tmsDescription: o.tmsDescription ?? undefined,
				atsDescription: o.atsDescription ?? undefined,
				factorySpecialNotes: o.factorySpecialNotes ?? undefined,
				generalNotes: o.generalNotes ?? undefined,
			});
		}
		// eslint-disable-next-line react-hooks/set-state-in-effect
		setHeader((prev) => ({
			...prev,
			companyId: isCopy ? "" : (o.companyId ?? ""),
			contactPersonId: isCopy ? "" : (o.contactPersonId ?? ""),
			sourceOfferId: o.sourceOfferId ?? "",
			orderDate: isCopy ? new Date().toISOString().slice(0, 10) : d(o.orderDate),
			deliveryDate: d(o.deliveryDate),
			currency: o.currency ?? prev.currency,
			paymentTerms: o.paymentTerms ?? "",
			deliveryTerms: o.deliveryTerms ?? "",
			shippingAddress: isCopy ? "" : (o.shippingAddress ?? ""),
			shippingAddressId: isCopy ? "" : (o.shippingAddressId ?? ""),
			notes: o.notes ?? "",
			startupDescription: o.startupDescription ?? "",
			installationDescription: o.installationDescription ?? "",
			status: o.status ?? "confirmed",
		}));
		// Üretilmiş satırları (pano seçimleri, ücretli aksesuarlar) ayıklayıp kendilerinden
		// önce gelen ana kaleme bağlarız; düzenlenebilir satır listesine girmezler.
		const all = [...(o.lines ?? [])].sort((a, b) => a.lineNumber - b.lineNumber);
		const sorted: typeof all = [];
		const generated: Record<number, GeneratedLine[]> = {};
		for (const line of all) {
			if (isGeneratedLine(line) && sorted.length > 0) {
				const parentIdx = sorted.length - 1;
				generated[parentIdx] = [...(generated[parentIdx] ?? []), toGeneratedLine(line)];
				continue;
			}
			sorted.push(line);
		}
		setGeneratedLines(generated);
		if (sorted.length > 0) {
			const mapped = sorted.map((l, i) => {
				const accessories = generated[i] ?? [];
				const panelAts = joinPanelText(accessories, ATS_PANEL_GROUPS);
				const panelTms = joinPanelText(accessories, TMS_PANEL_GROUPS);
				return {
					lineNumber: i + 1,
					itemCode: l.itemCode ?? "",
					itemDescription: l.itemDescription ?? "",
					displayDescription: l.displayDescription ?? "",
					lineDetail: l.lineDetail ?? "",
					quantity: l.quantity ?? "1",
					unit: l.unit ?? "",
					unitPrice: l.unitPrice ?? "",
					vatRate: l.vatRate ?? "20",
					vatGroup: l.vatGroup ?? "",
					discountPercent: l.discountPercent ?? "0",
					notes: l.notes ?? "",
					atsDescription: l.atsDescription || panelAts,
					tmsDescription: l.tmsDescription || panelTms,
					factorySpecialNotes: l.factorySpecialNotes ?? "",
					generalNotes: l.generalNotes ?? "",
					stockItemId: l.stockItemId ?? "",
					warehouseId: l.warehouseId ?? "",
					isCustomDesign: l.isCustomDesign ?? false,
				};
			});
			setLines(mapped);
			rememberStockItemOptions(mapped);
		}
		setPrefilled(true);
	}, [isEdit, copyFromId, prefilled, detail.data]);

	const addLine = () => {
		setLines([
			...lines,
			{
				lineNumber: lines.length + 1,
				itemCode: "",
				itemDescription: "",
				displayDescription: "",
				lineDetail: "",
				quantity: "1",
				unit: "",
				unitPrice: "",
				vatRate: "20",
				vatGroup: "",
				discountPercent: "0",
				notes: "",
				atsDescription: "",
				tmsDescription: "",
				factorySpecialNotes: "",
				generalNotes: "",
				stockItemId: "",
				warehouseId: "",
				isCustomDesign: false,
			},
		]);
	};

	const removeLine = (idx: number) => {
		setLines(lines.filter((_, i) => i !== idx).map((l, i) => ({ ...l, lineNumber: i + 1 })));
		setWarehouseStocks((prev) => {
			const copy: Record<number, StockItemWarehouse[]> = {};
			Object.keys(prev).forEach((key) => {
				const k = Number(key);
				const val = prev[k];
				if (!val) return;
				if (k < idx) copy[k] = val;
				if (k > idx) copy[k - 1] = val;
			});
			return copy;
		});
		// Ana kalem silinince altındaki üretilmiş satırlar da gider, sonrakiler kayar.
		setGeneratedLines((prev) => {
			const copy: Record<number, GeneratedLine[]> = {};
			Object.keys(prev).forEach((key) => {
				const k = Number(key);
				const val = prev[k];
				if (!val) return;
				if (k < idx) copy[k] = val;
				if (k > idx) copy[k - 1] = val;
			});
			return copy;
		});
		setAccessoryDraft((prev) => {
			const copy: typeof prev = {};
			Object.keys(prev).forEach((key) => {
				const k = Number(key);
				const val = prev[k];
				if (!val) return;
				if (k < idx) copy[k] = val;
				if (k > idx) copy[k - 1] = val;
			});
			return copy;
		});
	};

	// Ana kaleme elle ek ürün (ATS / Change Over ATS / CB panosu) eklemek için taslak alanlar.
	const [accessoryDraft, setAccessoryDraft] = useState<
		Record<number, { group: string; description: string; quantity: string }>
	>({});

	const accessoryDraftFor = (idx: number) =>
		accessoryDraft[idx] ?? { group: PANEL_GROUP_NAMES[0], description: "", quantity: "1" };

	const updateAccessoryDraft = (
		idx: number,
		field: "group" | "description" | "quantity",
		value: string,
	) => {
		setAccessoryDraft((prev) => ({
			...prev,
			[idx]: { ...accessoryDraftFor(idx), [field]: value },
		}));
	};

	const addAccessoryLine = (idx: number) => {
		const draftLine = accessoryDraftFor(idx);
		const option = draftLine.description.trim();
		if (!option) return;
		const qty = Number(draftLine.quantity) || 1;
		if (qty <= 0) return;
		const parent = lines[idx];
		const description = `${draftLine.group} - ${option}`;
		const accessory: GeneratedLine = {
			itemCode: "",
			itemDescription: description,
			displayDescription: description,
			lineDetail: "",
			quantity: String(qty),
			unit: "Adet",
			unitPrice: "0",
			vatRate: parent?.vatRate ?? "20",
			vatGroup: parent?.vatGroup ?? "",
			discountPercent: "0",
			notes: "",
			atsDescription: "",
			tmsDescription: "",
			factorySpecialNotes: "",
			generalNotes: "",
			stockItemId: "",
			warehouseId: parent?.warehouseId ?? "",
			isCustomDesign: parent?.isCustomDesign ?? false,
		};
		setGeneratedLines((prev) => ({ ...prev, [idx]: [...(prev[idx] ?? []), accessory] }));
		setAccessoryDraft((prev) => ({
			...prev,
			[idx]: { group: draftLine.group, description: "", quantity: "1" },
		}));
	};

	const removeAccessoryLine = (idx: number, gi: number) => {
		setGeneratedLines((prev) => ({
			...prev,
			[idx]: (prev[idx] ?? []).filter((_, i) => i !== gi),
		}));
	};

	const updateLine = (idx: number, field: keyof LineItem, value: string) => {
		const next = [...lines];
		next[idx] = { ...next[idx], [field]: value } as LineItem;
		setLines(next);
	};

	const calcLineTotal = (line: Omit<LineItem, "lineNumber">) => {
		const qty = Number(line.quantity) || 0;
		const price = Number(line.unitPrice) || 0;
		const vat = Number(line.vatRate) || 0;
		const disc = Number(line.discountPercent) || 0;
		const base = qty * price * (1 - disc / 100);
		return base * (1 + vat / 100);
	};

	// Ücretli aksesuar satırları da tutara girer; düzenlenemez olmaları toplamdan
	// düşülmelerini gerektirmez.
	const linesForTotals: Omit<LineItem, "lineNumber">[] = [
		...lines,
		...Object.values(generatedLines).flat(),
	];
	const subtotal = linesForTotals.reduce(
		(sum, l) =>
			sum +
			(Number(l.quantity) || 0) *
				(Number(l.unitPrice) || 0) *
				(1 - (Number(l.discountPercent) || 0) / 100),
		0,
	);
	const vatTotal = linesForTotals.reduce(
		(sum, l) =>
			sum +
			(Number(l.quantity) || 0) *
				(Number(l.unitPrice) || 0) *
				(1 - (Number(l.discountPercent) || 0) / 100) *
				((Number(l.vatRate) || 0) / 100),
		0,
	);
	const total = subtotal + vatTotal;

	const loadCompanies = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{
					id: string;
					legalName: string;
					currency: string | null;
					sapAccounts?: { currency: string; sapCardCode: string | null }[];
				}>;
			}>(
				// Başkası adına sipariş açılırken yalnızca seçilen kişinin carileri listelenir.
				`/api/companies?limit=50${onBehalfOf ? `&salesPerson=${encodeURIComponent(onBehalfOf)}&includePublic=true` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.flatMap((c) => {
				const currencies = c.sapAccounts?.length
					? [...new Set(c.sapAccounts.map((a) => a.currency).filter(Boolean))]
					: c.currency
						? [c.currency]
						: [];
				if (currencies.length === 0) {
					return [{ value: c.id, label: c.legalName, meta: { currency: "" } }];
				}
				return currencies.map((currency) => ({
					value: `${c.id}::${currency}`,
					label: `${c.legalName} (${currency})`,
					meta: { currency },
				}));
			});
		},
		[onBehalfOf],
	);

	const loadPersons = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{ id: string; firstName: string; lastName: string }>;
			}>(
				`/api/persons?limit=50${header.companyId ? `&companyId=${header.companyId}` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }));
		},
		[header.companyId],
	);

	const loadOffers = useCallback(
		async (query: string) => {
			const res = await api.get<{
				items: Array<{ id: string; documentNumber: string | null; companyName: string | null }>;
			}>(
				`/api/offers?limit=50${header.companyId ? `&companyId=${header.companyId}` : ""}${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			return res.items.map((o) => ({
				value: o.id,
				label: o.companyName
					? `${o.companyName} — ${o.documentNumber || o.id.slice(0, 8)}`
					: o.documentNumber || o.id.slice(0, 8),
			}));
		},
		[header.companyId],
	);

	const loadStockItems = useCallback(async (query: string) => {
		const res = await api.get<{
			items: Array<{ id: string; code: string; name: string; uom: string | null }>;
		}>(
			`/api/stock-items?limit=50&activeOnly=true${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((s) => ({
			value: s.id,
			label: `${s.code} — ${s.name}`,
			meta: { code: s.code, name: s.name, uom: s.uom ?? "" },
		}));
	}, []);

	const handleSelectStockItem = async (
		idx: number,
		stockItemId: string,
		option?: { label: string; meta?: Record<string, string> },
	) => {
		const next = [...lines];
		const line = next[idx];
		if (!line) return;

		// Görünen ad elle değiştirilmediyse (yani önceki ürünün adıysa) yeni ürünle
		// birlikte güncellenir; kullanıcının yazdığı özel metin korunur.
		const isAutoDisplay =
			!line.displayDescription || line.displayDescription === line.itemDescription;

		if (!stockItemId || !option) {
			next[idx] = {
				...line,
				stockItemId: "",
				itemCode: "",
				itemDescription: "",
				...(isAutoDisplay ? { displayDescription: "" } : {}),
				unit: "",
				warehouseId: "",
			};
			setLines(next);
			setWarehouseStocks((prev) => {
				const copy = { ...prev };
				delete copy[idx];
				return copy;
			});
			return;
		}

		const parts = option.label.split(" — ");
		const code = parts[0] ?? "";
		const name = parts[1] ?? "";
		next[idx] = {
			...line,
			stockItemId,
			itemCode: code,
			itemDescription: name,
			displayDescription: isAutoDisplay ? name : line.displayDescription,
			unit: option.meta?.uom || line.unit,
		};
		setLines(next);

		try {
			const res = await api.get<{
				warehouseStocks: StockItemWarehouse[];
				defaultWarehouseCode: string | null;
				salesVatGroup: string | null;
			}>(`/api/stock-items/${stockItemId}`);
			const stocks = res.warehouseStocks ?? [];
			setWarehouseStocks((prev) => ({ ...prev, [idx]: stocks }));
			// Ürünün SAP satış KDV grubunu varsayılan seç, oranı da ata
			if (res.salesVatGroup) {
				const g = vatGroups.find((v) => v.code === res.salesVatGroup);
				setLines((prevLines) => {
					const updated = [...prevLines];
					if (updated[idx]) {
						updated[idx] = {
							...updated[idx],
							vatGroup: res.salesVatGroup as string,
							...(g ? { vatRate: String(g.rate) } : {}),
						};
					}
					return updated;
				});
			}
			let defaultWhId = "";
			if (res.defaultWarehouseCode) {
				const match = allWarehouses.find((w) => w.code === res.defaultWarehouseCode);
				if (match) defaultWhId = match.id;
			}
			if (!defaultWhId && stocks.length > 0) {
				const first = stocks[0]!;
				const maxStock = stocks.reduce(
					(max, s) => (Number(s.currentStock) > Number(max.currentStock) ? s : max),
					first,
				);
				defaultWhId = maxStock.warehouseId;
			}
			if (defaultWhId) {
				setLines((prevLines) => {
					const updated = [...prevLines];
					if (updated[idx]) {
						updated[idx] = { ...updated[idx], warehouseId: defaultWhId };
					}
					return updated;
				});
			}
		} catch {
			setWarehouseStocks((prev) => {
				const copy = { ...prev };
				delete copy[idx];
				return copy;
			});
		}
	};

	const submit = () => {
		setFormError("");
		const errors: Record<string, string> = {};
		// Eksik alanların adları toplanır: sayfa başındaki uyarı kaydırılmadan görünmediği
		// için kullanıcıya neyin eksik olduğunu bildirimde de söyleriz.
		const missingLabels: string[] = [];
		if (!header.companyId) {
			errors.companyId = t("requiredField");
			missingLabels.push(t("company"));
		}
		if (!header.orderDate) {
			errors.orderDate = t("requiredField");
			missingLabels.push(t("orderDate"));
		}
		if (!header.deliveryDate) {
			errors.deliveryDate = t("requiredField");
			missingLabels.push(t("deliveryDate"));
		}
		if (!header.paymentTerms.trim()) {
			errors.paymentTerms = t("requiredField");
			missingLabels.push(t("paymentTerms"));
		}
		setFieldErrors(errors);
		if (missingLabels.length > 0) {
			const message = t("missingFields", { fields: missingLabels.join(", ") });
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const missingStockItem = lines.findIndex((l) => !l.stockItemId);
		if (missingStockItem !== -1) {
			const message = t("stockItemRequiredLine", { line: missingStockItem + 1 });
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const invalidLine = lines.findIndex((l) => !l.quantity || Number(l.quantity) <= 0);
		if (invalidLine !== -1) {
			const message = t("quantityRequiredLine", { line: invalidLine + 1 });
			setFormError(message);
			showToast(message, "error");
			return;
		}
		const payload = {
			...(isEdit ? {} : copyExtras),
			...header,
			orderDate: header.orderDate || undefined,
			deliveryDate: header.deliveryDate || undefined,
			companyId: header.companyId || undefined,
			contactPersonId: header.contactPersonId || undefined,
			sourceOfferId: header.sourceOfferId || undefined,
			shippingAddress: header.shippingAddress || undefined,
			shippingAddressId: header.shippingAddressId || undefined,
			assignedTo: !isEdit && onBehalfOf ? onBehalfOf : undefined,
			subtotal,
			vatTotal,
			total,
			// Üretilmiş satırlar ana kalemlerinin hemen ardına, geldikleri sırayla geri yazılır.
			lines: lines
				.flatMap((l, idx) => [l, ...(generatedLines[idx] ?? [])])
				.map((l, i) => ({
					lineNumber: i + 1,
					itemCode: l.itemCode || undefined,
					itemDescription: l.itemDescription || undefined,
					displayDescription: l.displayDescription || undefined,
					lineDetail: l.lineDetail || undefined,
					quantity: Number(l.quantity) || 1,
					unit: l.unit || undefined,
					unitPrice: Number(l.unitPrice) || 0,
					vatRate: l.vatRate ? Number(l.vatRate) : undefined,
					vatGroup: l.vatGroup || undefined,
					discountPercent: l.discountPercent ? Number(l.discountPercent) : undefined,
					lineTotal: calcLineTotal(l),
					notes: l.notes || undefined,
					atsDescription: l.atsDescription || undefined,
					tmsDescription: l.tmsDescription || undefined,
					factorySpecialNotes: l.factorySpecialNotes || undefined,
					generalNotes: l.generalNotes || undefined,
					isCustomDesign: l.isCustomDesign,
					stockItemId: l.stockItemId || undefined,
					warehouseId: l.warehouseId || undefined,
				})),
		};

		const onError = (err: unknown) => {
			setFormError(err instanceof Error ? err.message : t("error"));
		};

		if (isEdit && editId) {
			updateOrder.mutate(
				{ id: editId, data: payload },
				{
					// Kaydetme sonucu artık toast ile bildiriliyor (SAP'ye yansıma durumu dahil),
					// bu yüzden bloklayan alert kaldırıldı.
					onSuccess: async (updated) => {
						if (pendingFiles.length > 0) {
							await uploadAttachments.mutateAsync({ orderId: updated.id, files: pendingFiles });
						}
						for (const item of removedAttachments) {
							await deleteAttachment.mutateAsync({ orderId: updated.id, item });
						}
						navigate({ to: "/orders/$id", params: { id: updated.id } });
					},
					onError,
				},
			);
		} else {
			createOrder.mutate(payload, {
				onSuccess: async (created) => {
					if (pendingFiles.length > 0) {
						await uploadAttachments.mutateAsync({ orderId: created.id, files: pendingFiles });
					}
					draft.clear();
					alert(t("createSuccess"));
					navigate({
						to: "/orders",
						search: {
							page: 1,
							size: 20,
							status: "",
							docNo: "",
							companyId: "",
							product: "",
							assignedTo: undefined,
							orderDateFrom: "",
							orderDateTo: "",
							country: "",
						},
					});
				},
				onError,
			});
		}
	};

	return (
		<div>
			<PageHeader title={isEdit ? t("editOrder") : t("newOrder")} />

			{draft.showPrompt && (
				<Card>
					<div className={styles.draftPrompt}>
						<span>Kaydedilmemiş bir taslak bulundu. Verileriniz geri yüklensin mi?</span>
						<div className={styles.draftPromptActions}>
							<Button onClick={restoreDraft}>Evet</Button>
							<Button variant="ghost" onClick={draft.discardDraft}>
								Hayır
							</Button>
						</div>
					</div>
				</Card>
			)}

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.formGrid}>
						{formError && <div className={styles.formError}>{formError}</div>}
						{canDelegate && !isEdit && (
							<FormField label={t("onBehalfOf")}>
								<SearchableSelect
									options={delegatableUsers.map((u) => ({ value: u.id, label: u.name }))}
									value={onBehalfOf}
									onChange={(val) => {
										if (val === onBehalfOf) return;
										setOnBehalfOf(val);
										// Önceki kişinin carisi yeni kişiye ait olmayabilir.
										setHeader({
											...header,
											companyId: "",
											contactPersonId: "",
											sourceOfferId: "",
											shippingAddressId: "",
											shippingAddress: "",
										});
										setInitialOptions((prev) => ({
											...prev,
											company: undefined,
											person: undefined,
											offer: undefined,
										}));
									}}
									placeholder={t("onBehalfOfSelf")}
									clearable
								/>
							</FormField>
						)}
						<FormField label={t("company")} required>
							<SearchableSelect
								value={header.companyId ? `${header.companyId}::${header.currency}` : ""}
								onChange={(val, option) => {
									const companyCurrency = option?.meta?.["currency"];
									const companyId = val.split("::")[0] ?? "";
									companyCurrencyPickedRef.current = !!companyCurrency;
									setHeader({
										...header,
										companyId,
										contactPersonId: "",
										sourceOfferId: "",
										shippingAddressId: "",
										shippingAddress: "",
										...(companyCurrency ? { currency: companyCurrency } : {}),
									});
									setInitialOptions((prev) => ({
										...prev,
										person: undefined,
										offer: undefined,
									}));
								}}
								loadOptions={loadCompanies}
								refetchKey={onBehalfOf}
								initialOption={initialOptions.company}
								placeholder={t("selectCompany")}
								clearable
							/>
						</FormField>
						<FormField label={t("authorizedPerson")}>
							<SearchableSelect
								value={header.contactPersonId}
								onChange={(val) => setHeader({ ...header, contactPersonId: val })}
								loadOptions={loadPersons}
								refetchKey={header.companyId}
								initialOption={initialOptions.person}
								placeholder={t("selectPerson")}
								clearable
								disabled={!header.companyId}
							/>
						</FormField>
						<FormField label={t("offer")}>
							<SearchableSelect
								value={header.sourceOfferId}
								onChange={(val) => setHeader({ ...header, sourceOfferId: val })}
								loadOptions={loadOffers}
								refetchKey={header.companyId}
								initialOption={initialOptions.offer}
								placeholder={t("selectOffer")}
								clearable
								disabled={!header.companyId}
							/>
						</FormField>
						<FormField label={t("orderDate")} required>
							<Input
								type="date"
								value={header.orderDate}
								onChange={(e) => setHeader({ ...header, orderDate: e.target.value })}
								error={fieldErrors.orderDate}
							/>
						</FormField>
						<FormField label={t("deliveryDate")} required>
							<Input
								type="date"
								value={header.deliveryDate}
								onChange={(e) => setHeader({ ...header, deliveryDate: e.target.value })}
								error={fieldErrors.deliveryDate}
							/>
						</FormField>
						<FormField label={t("currency")}>
							<Input
								value={header.currency}
								readOnly
								style={{ background: "var(--color-bg-sunken)", cursor: "default" }}
							/>
						</FormField>
						<FormField label={t("paymentTerms")} required>
							<Input
								value={header.paymentTerms}
								onChange={(e) => setHeader({ ...header, paymentTerms: e.target.value })}
								error={fieldErrors.paymentTerms}
							/>
						</FormField>
						<FormField label={t("shippingAddress")}>
							{header.companyId ? (
								<ShippingAddressPicker
									companyId={header.companyId}
									value={header.shippingAddressId}
									onChange={(addressId, formatted) =>
										setHeader({
											...header,
											shippingAddressId: addressId,
											shippingAddress: formatted,
										})
									}
								/>
							) : (
								<Input value="" readOnly placeholder={t("selectCompanyFirst")} />
							)}
						</FormField>
						<FormField label={`${t("orderGeneralDescription")} (${header.notes.length}/254)`}>
							<textarea
								className={styles.textarea}
								rows={1}
								maxLength={254}
								value={header.notes}
								onChange={(e) => setHeader({ ...header, notes: e.target.value })}
							/>
						</FormField>
						<FormField label={t("attachments")}>
							<FileDropzone files={pendingFiles} onChange={setPendingFiles} />
							{isEdit && (existingAttachments.data?.items.length ?? 0) > 0 && (
								<div className={styles.existingAttachments}>
									{existingAttachments
										.data!.items.filter((item) => !isRemovedAttachment(item))
										.map((item) => (
											<div
												key={`${item.source}-${item.source === "sap" ? item.line : item.index}`}
												className={styles.existingAttachmentLink}
											>
												<a
													href={orderAttachmentDownloadUrl(editId as string, item, {
														inline: true,
													})}
													target="_blank"
													rel="noreferrer"
													className={styles.existingAttachmentName}
												>
													{item.name}
												</a>
												<a
													href={orderAttachmentDownloadUrl(editId as string, item)}
													aria-label={t("download")}
													className={styles.existingAttachmentDownload}
												>
													<Download size={14} />
												</a>
												<button
													type="button"
													aria-label={t("remove")}
													className={styles.existingAttachmentRemove}
													onClick={() => setRemovedAttachments((prev) => [...prev, item])}
												>
													<X size={14} />
												</button>
											</div>
										))}
								</div>
							)}
						</FormField>
						<div className={styles.checkboxRow} style={{ alignSelf: "end" }}>
							<label className={styles.checkboxLabel}>
								<input
									type="checkbox"
									checked={lines.length > 0 && lines.every((l) => l.isCustomDesign)}
									onChange={(e) => {
										const checked = e.target.checked;
										setLines((prev) => prev.map((l) => ({ ...l, isCustomDesign: checked })));
									}}
								/>
								<span>{t("allCustomDesign")}</span>
							</label>
						</div>
					</div>
					{editId && (
						<FormField label={t("status")}>
							<Select
								options={[
									...(header.status && !["confirmed", "closed", "cancelled"].includes(header.status)
										? [
												{
													value: header.status,
													label: ORDER_STATUS_LABELS[header.status] ?? "Açık",
												},
											]
										: []),
									{ value: "confirmed", label: "Açık" },
									{ value: "closed", label: "Kapalı" },
									{ value: "cancelled", label: "İptal Edildi" },
								]}
								value={header.status ?? "confirmed"}
								onChange={(e) => setHeader({ ...header, status: e.target.value })}
							/>
						</FormField>
					)}
				</Card>

				<Card header={t("lineItems")}>
					{lines.map((line, idx) => (
						<div key={idx} className={styles.lineRow}>
							<div className={styles.lineGrid}>
								<FormField label={t("stockItem")}>
									<SearchableSelect
										value={line.stockItemId}
										onChange={(val, option) => handleSelectStockItem(idx, val, option)}
										loadOptions={loadStockItems}
										initialOption={
											line.stockItemId ? stockItemOptions[line.stockItemId] : undefined
										}
										placeholder={t("selectStockItem")}
										clearable
									/>
								</FormField>
								<FormField label={t("itemCode")}>
									<Input
										placeholder={t("itemCode")}
										value={line.itemCode}
										onChange={(e) => updateLine(idx, "itemCode", e.target.value)}
									/>
								</FormField>
								<FormField label={t("itemDescription")}>
									<Input
										placeholder={t("itemDescription")}
										value={line.itemDescription}
										readOnly
										style={{ background: "var(--color-bg-sunken)", cursor: "default" }}
									/>
								</FormField>
								<FormField label={t("displayDescription")}>
									<Input
										placeholder={t("displayDescription")}
										value={line.displayDescription}
										onChange={(e) => updateLine(idx, "displayDescription", e.target.value)}
									/>
								</FormField>
								<FormField label={t("lineDetail")}>
									<textarea
										className={styles.textarea}
										rows={2}
										value={line.lineDetail}
										onChange={(e) => updateLine(idx, "lineDetail", e.target.value)}
									/>
								</FormField>
								<FormField label={t("atsDescription")}>
									<textarea
										className={styles.textarea}
										rows={2}
										value={line.atsDescription}
										onChange={(e) => updateLine(idx, "atsDescription", e.target.value)}
									/>
								</FormField>
								<FormField label={t("tmsDescription")}>
									<textarea
										className={styles.textarea}
										rows={2}
										value={line.tmsDescription}
										onChange={(e) => updateLine(idx, "tmsDescription", e.target.value)}
									/>
								</FormField>
								<FormField
									label={`${t("factorySpecialNotes")} (${line.factorySpecialNotes?.length ?? 0}/254)`}
								>
									<textarea
										className={styles.textarea}
										rows={2}
										maxLength={254}
										value={line.factorySpecialNotes}
										onChange={(e) => updateLine(idx, "factorySpecialNotes", e.target.value)}
									/>
								</FormField>
								<FormField
									label={`${t("lineGeneralNotes")} (${line.generalNotes?.length ?? 0}/150)`}
								>
									<textarea
										className={styles.textarea}
										rows={2}
										maxLength={150}
										value={line.generalNotes}
										onChange={(e) => updateLine(idx, "generalNotes", e.target.value)}
									/>
								</FormField>
								{allWarehouses.length > 0 && (
									<FormField label={t("warehouse")}>
										<Select
											options={[
												{ value: "", label: "—" },
												...allWarehouses.map((w) => {
													const stock = warehouseStocks[idx]?.find(
														(s) => s.warehouseId === w.id,
													)?.currentStock;
													const label = `${w.code} - ${w.name}`;
													return {
														value: w.id,
														label:
															stock != null ? `${label} — ${t("stockQuantity")}: ${stock}` : label,
													};
												}),
											]}
											value={line.warehouseId}
											onChange={(e) => updateLine(idx, "warehouseId", e.target.value)}
										/>
									</FormField>
								)}
								<FormField label={t("quantity")}>
									<Input
										placeholder={t("quantity")}
										type="number"
										value={line.quantity}
										onChange={(e) => updateLine(idx, "quantity", e.target.value)}
									/>
								</FormField>
								<FormField label={t("unit")}>
									<Input
										placeholder={t("unit")}
										value={line.unit}
										readOnly
										disabled
										style={{ background: "var(--color-bg-sunken)", cursor: "default" }}
									/>
								</FormField>
								<FormField label={t("unitPrice")}>
									<Input
										placeholder={t("unitPrice")}
										type="number"
										value={line.unitPrice}
										onChange={(e) => updateLine(idx, "unitPrice", e.target.value)}
									/>
								</FormField>
								<FormField label={t("vatRate")}>
									<SearchableSelect
										value={line.vatGroup}
										options={vatGroupOptions}
										initialOption={
											line.vatGroup && !vatGroups.some((v) => v.code === line.vatGroup)
												? { value: line.vatGroup, label: line.vatGroup }
												: undefined
										}
										placeholder={t("search")}
										clearable
										onChange={(code) => {
											const g = vatGroups.find((v) => v.code === code);
											setLines((prev) => {
												const next = [...prev];
												if (next[idx])
													next[idx] = {
														...next[idx],
														vatGroup: code,
														...(g ? { vatRate: String(g.rate) } : {}),
													};
												return next;
											});
										}}
									/>
								</FormField>
								<FormField label={t("discountPercent")}>
									<Input
										placeholder={t("discountPercent")}
										type="number"
										value={line.discountPercent}
										onChange={(e) => updateLine(idx, "discountPercent", e.target.value)}
									/>
								</FormField>
								<FormField label="" className={styles.customDesignField}>
									<label className={styles.checkboxLabel}>
										<input
											type="checkbox"
											checked={line.isCustomDesign}
											onChange={(e) => {
												const next = [...lines];
												next[idx] = { ...next[idx], isCustomDesign: e.target.checked } as LineItem;
												setLines(next);
											}}
										/>
										<span>{t("isCustomDesign")}</span>
									</label>
								</FormField>
							</div>
							<div className={styles.lineActions}>
								<span className={styles.lineTotal}>{calcLineTotal(line).toFixed(2)}</span>
								{lines.length > 1 && (
									<Button
										variant="ghost"
										size="sm"
										className={styles.removeLineBtn}
										onClick={() => removeLine(idx)}
									>
										<Minus size={16} />
									</Button>
								)}
							</div>
							<div className={styles.generatedLines}>
								{(generatedLines[idx] ?? []).map((g, gi) => (
									<div key={gi} className={styles.generatedLine}>
										<span>{generatedLineLabel(g)}</span>
										<span className={styles.generatedLineRight}>
											{calcLineTotal(g).toFixed(2)}
											<Button
												variant="ghost"
												size="sm"
												onClick={() => removeAccessoryLine(idx, gi)}
											>
												<X size={14} />
											</Button>
										</span>
									</div>
								))}
								{isEdit && (
									<div className={styles.accessoryForm}>
										<Select
											value={accessoryDraftFor(idx).group}
											onChange={(e) => updateAccessoryDraft(idx, "group", e.target.value)}
											options={PANEL_GROUP_NAMES.map((g) => ({ value: g, label: g }))}
										/>
										<Input
											placeholder="Ek ürün açıklaması"
											value={accessoryDraftFor(idx).description}
											onChange={(e) => updateAccessoryDraft(idx, "description", e.target.value)}
										/>
										<Input
											type="number"
											min="1"
											value={accessoryDraftFor(idx).quantity}
											onChange={(e) => updateAccessoryDraft(idx, "quantity", e.target.value)}
										/>
										<Button
											variant="secondary"
											size="sm"
											onClick={() => addAccessoryLine(idx)}
											disabled={!accessoryDraftFor(idx).description.trim()}
										>
											<Plus size={14} />
										</Button>
									</div>
								)}
							</div>
						</div>
					))}
					<Button variant="secondary" className={styles.addLineBtn} onClick={addLine}>
						<Plus size={16} />
					</Button>
				</Card>

				<Card>
					<div className={styles.totals}>
						<div className={styles.totalRow}>
							<span>{t("subtotal")}</span>
							<span>
								{subtotal.toFixed(2)} {header.currency}
							</span>
						</div>
						<div className={styles.totalRow}>
							<span>{t("vatTotal")}</span>
							<span>
								{vatTotal.toFixed(2)} {header.currency}
							</span>
						</div>
						<div className={[styles.totalRow, styles.grandTotal].join(" ")}>
							<span>{t("total")}</span>
							<span>
								{total.toFixed(2)} {header.currency}
							</span>
						</div>
					</div>
					<div className={styles.submitRow}>
						<Button
							onClick={submit}
							isLoading={isEdit ? updateOrder.isPending : createOrder.isPending}
						>
							{isEdit ? t("save") : t("create")}
						</Button>
					</div>
				</Card>
			</div>
		</div>
	);
}
